import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebase/firebase';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { Form, Button, Container } from 'react-bootstrap';
import Select from 'react-select';
import { components } from 'react-select';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';

const EditLicense = () => {
  const [licenseId, setLicenseId] = useState('');
  const [softwareName, setSoftwareName] = useState('');
  const [softwareCompany, setSoftwareCompany] = useState('');
  const [licenseType, setLicenseType] = useState(''); // Subscription or Fully Paid
  const [expiryDate, setExpiryDate] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');

  const [fullPrice, setFullPrice] = useState('');
 
  const [seats, setSeats] = useState(1);
  const [seatAssignments, setSeatAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [quoteUrl, setQuoteUrl] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');
  const [hasSupport, setHasSupport] = useState(false); // Support availability
  const [supportDetails, setSupportDetails] = useState({ fullName: '', email: '', phone: '' });
  const navigate = useNavigate();
  const { id } = useParams();

  // Fetch employees from Firestore
  useEffect(() => {
    const fetchEmployees = async () => {
      const querySnapshot = await getDocs(collection(db, 'employees'));
      setEmployees(querySnapshot.docs.map((doc) => ({ id: doc.id, full_name: doc.data().full_name })));
    };
    fetchEmployees();
  }, []);

  // Fetch the license data from Firestore
  useEffect(() => {
    const fetchLicenseData = async () => {
      const licenseDoc = doc(db, 'licenses', id);
      const licenseSnapshot = await getDoc(licenseDoc);
      if (licenseSnapshot.exists()) {
        const licenseData = licenseSnapshot.data();
        setLicenseId(licenseData.license_id);
        setSoftwareName(licenseData.software_name);
        setSoftwareCompany(licenseData.software_company);
        setLicenseType(licenseData.license_type);
        setExpiryDate(licenseData.expiry_date || '');
        setPurchaseDate(licenseData.purchase_date || '');
       
        setFullPrice(licenseData.full_price || '');
       
        setSeats(licenseData.seats);
        setSeatAssignments(licenseData.seat_assignment || []);
        setQuoteUrl(licenseData.quote_url || '');
        setReceiptUrl(licenseData.receipt_url || '');
        setHasSupport(!!licenseData.support);
        setSupportDetails(licenseData.support || { fullName: '', email: '', phone: '' });
      }
    };
    fetchLicenseData();
  }, [id]);

  // Handle seat assignment dropdown with checkboxes
  const handleSelectChange = (selectedOptions) => {
    const selectedEmployeeIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSeatAssignments(selectedEmployeeIds);
  };

  // Handle the file upload to Firebase storage
  const uploadFile = async (file, fileType) => {
    if (!file) return;
    const storageRef = ref(storage, `licenses/${licenseId}/${fileType}`);
    await uploadBytes(storageRef, file);
    const fileUrl = await getDownloadURL(storageRef);
    if (fileType === 'quote') {
      setQuoteUrl(fileUrl);
    } else if (fileType === 'receipt') {
      setReceiptUrl(fileUrl);
    }
    // Update the Firestore with the new file URL
    await updateLicenseDocument(fileType, fileUrl);
  };

  // Remove the entire directory for the given file type
  const removeDirectory = async (fileType) => {
    try {
      const folderRef = ref(storage, `licenses/${licenseId}/${fileType}`);
      const listResult = await listAll(folderRef);
      // Delete all files in the folder
      for (const item of listResult.items) {
        await deleteObject(item);
      }
      console.log(`Directory for ${fileType} deleted successfully`);
      // Reset the URL state for the removed file
      if (fileType === 'quote') {
        setQuoteUrl('');
      } else if (fileType === 'receipt') {
        setReceiptUrl('');
      }
    } catch (error) {
      console.error('Error removing directory:', error.message);
    }
  };

  // Helper function to update the Firestore document with the new file URL
  const updateLicenseDocument = async (fileType, fileUrl) => {
    const licenseRef = doc(db, 'licenses', id);
    const updateData = fileType === 'quote' ? { quote_url: fileUrl } : { receipt_url: fileUrl };
    await updateDoc(licenseRef, updateData);
  };

  // Handle the form submission to update the license
  const handleSubmit = async (e) => {
    e.preventDefault();
    const licenseRef = doc(db, 'licenses', id);
    await updateDoc(licenseRef, {
      license_id: licenseId,
      software_name: softwareName,
      software_company: softwareCompany,
      license_type: licenseType,
      expiry_date: licenseType === 'subscription' ? expiryDate : null,
      purchase_date: purchaseDate,
      full_price: fullPrice,
     
      seats: seats,
      seat_assignment: seatAssignments,
      quote_url: quoteUrl,
      receipt_url: receiptUrl,
      support: hasSupport ? supportDetails : null,
    });
    navigate('/dashboard'); // Navigate back to dashboard
  };

  const customSingleValue = ({ data }) => (
    <div className="custom-single-value">
      {data.label}
    </div>
  );

  const customOption = (props) => {
    return (
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null} // Do nothing here since we handle the change with handleSelectChange
        />
        <label>{props.label}</label>
      </components.Option>
    );
  };

  // Cancel the form and go back to dashboard
  const handleCancel = () => {
    navigate('/dashboard'); // Go back to dashboard or any other page
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">ערוך רשיון</h2>
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>מזהה רשיון</Form.Label>
          <Form.Control
            type="text"
            placeholder="הכנס מזהה רשיון"
            value={licenseId}
            onChange={(e) => setLicenseId(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>שם תוכנה</Form.Label>
          <Form.Control
            type="text"
            placeholder="הכנס שם תוכנה"
            value={softwareName}
            onChange={(e) => setSoftwareName(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>חברה</Form.Label>
          <Form.Control
            type="text"
            placeholder="הכנס שם חברה"
            value={softwareCompany}
            onChange={(e) => setSoftwareCompany(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>סוג רשיון</Form.Label>
          <Form.Select
            value={licenseType}
            onChange={(e) => setLicenseType(e.target.value)}
            required
          >
            <option value="">בחר סוג רשיון</option>
            <option value="subscription">מנוי</option>
            <option value="fully_paid">רכישה מלאה</option>
          </Form.Select>
        </Form.Group>

        {licenseType === 'subscription' && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>תאריך תפוגה</Form.Label>
              <Form.Control
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                required
              />
            </Form.Group>

            
          </>
        )}

        

        <Form.Group className="mb-3">
          <Form.Label>מחיר מלא</Form.Label>
          <Form.Control
            type="number"
            value={fullPrice}
            onChange={(e) => setFullPrice(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>תאריך רכישה</Form.Label>
          <Form.Control
            type="date"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            required
          />
        </Form.Group>

       

        <Form.Group className="mb-3">
          <Form.Label>מספר מושבים</Form.Label>
          <Form.Control
            type="number"
            min="1"
            value={seats}
            onChange={(e) => {
              setSeats(Number(e.target.value)); // Update the number of seats
              if (seatAssignments.length > Number(e.target.value)) {
                setSeatAssignments(seatAssignments.slice(0, Number(e.target.value))); // Trim assignments if needed
              }
            }}
            required
          />
        </Form.Group>

        <h5>שיוך מושבים</h5>
        <Select
          isMulti
          options={employees.map(employee => ({
            value: employee.id,
            label: employee.full_name
          }))}
          onChange={handleSelectChange}
          value={seatAssignments.map(id => ({
            value: id,
            label: employees.find(emp => emp.id === id)?.full_name
          }))}
          components={{ Option: customOption, SingleValue: customSingleValue }}
          isDisabled={seatAssignments.length >= seats}
        />

        <h5>העלה הצעת מחיר</h5>
        <input
          type="file"
          onChange={(e) => uploadFile(e.target.files[0], 'quote')}
        />
        {quoteUrl && (
          <div>
            <a href={quoteUrl} target="_blank" rel="noopener noreferrer">
              הצעת מחיר שהועלתה
            </a>
            <Button onClick={() => removeDirectory('quote')}>הסר הצעת מחיר</Button>
          </div>
        )}

        <h5>העלה חשבונית</h5>
        <input
          type="file"
          onChange={(e) => uploadFile(e.target.files[0], 'receipt')}
        />
        {receiptUrl && (
          <div>
            <a href={receiptUrl} target="_blank" rel="noopener noreferrer">
              חשבונית שהועלתה
            </a>
            <Button onClick={() => removeDirectory('receipt')}>הסר חשבונית</Button>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            label="האם יש תמיכה?"
            checked={hasSupport}
            onChange={(e) => setHasSupport(e.target.checked)}
          />
        </Form.Group>

        {hasSupport && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>שם מלא</Form.Label>
              <Form.Control
                type="text"
                placeholder="הכנס שם מלא"
                value={supportDetails.fullName}
                onChange={(e) => setSupportDetails({ ...supportDetails, fullName: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>אימייל</Form.Label>
              <Form.Control
                type="email"
                placeholder="הכנס אימייל"
                value={supportDetails.email}
                onChange={(e) => setSupportDetails({ ...supportDetails, email: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>טלפון</Form.Label>
              <Form.Control
                type="tel"
                placeholder="הכנס טלפון"
                value={supportDetails.phone}
                onChange={(e) => setSupportDetails({ ...supportDetails, phone: e.target.value })}
                required
              />
            </Form.Group>
          </>
        )}

        <Button variant="primary" type="submit" className="mr-2">
          עדכן רשיון
        </Button>
        <Button variant="secondary" type="button" onClick={handleCancel}>
          ביטול
        </Button>
      </Form>
    </Container>
  );
};

export default EditLicense;
